import {
    DimensionValueNameDisplay,
    EntityKey,
    SheetFilterStateReduced,
    SheetFilterDimensionRepresentationReduced,
    SheetFilterData,
    TileDataDimensionSlicer,
} from '@common/api';
import { Tooltip } from '@components/tooltip/Tooltip';
import { useEntity } from '@data/entity/entity';
import { dimensionTagToEntityKey } from '@data/entity/legacy';
import { getEntityLongName } from '@data/entity/util';
import useDebouncedResizeObserver from '@hooks/useDebouncedResizeObserver';
import { Update } from '@use-gpu/state';
import { $updateItem } from '@util/patch';
import { CSSProperties, memo, useMemo, useRef } from 'react';
import { FixedSizeGrid } from 'react-window';
import { DashboardTileContent } from '../Dashboard.style';
import { DimensionSlicerButton, DimensionSlicerButtonTitle } from './Tiles.style';

export const DimensionSlicerTile = memo(
    ({
        data,
        filter,
        updateFilters,
    }: {
        data: TileDataDimensionSlicer;
        canEdit: boolean;
        filter?: SheetFilterStateReduced;
        updateFilters: (update: Update<SheetFilterData[]>) => void;
    }) => {
        const { ref: widgetRef, width, height } = useDebouncedResizeObserver(500, true);
        const dIds = filter?.dimensions || [];

        const space = 5;
        const [colWidth, colCount, rowHeight, rowCount] = useMemo(() => {
            const cw = 126;
            const rh = 30;

            let cc = width ? Math.floor(width / cw) : 1;
            if (cc === 0) {
                cc = 1;
            }
            const rc = cc > 1 ? Math.ceil(dIds.length / cc) : dIds.length;

            return [cw, cc, rh, rc];
        }, [width, dIds.length]);

        function updateFilter(tableId: number, filter: Update<SheetFilterData>) {
            updateFilters($updateItem((f) => f.tableId === tableId, filter));
        }

        const toggleTag = (tagId: number) => {
            if (filter === undefined) {
                return;
            }

            let newIds;
            if (filter.multiselect) {
                newIds = dIds.reduce((memo, d) => {
                    if (d.selected) {
                        memo.push(d.id);
                    }
                    return memo;
                }, [] as number[]);

                if (newIds.includes(tagId)) {
                    newIds = newIds.filter((tId) => tId !== tagId);
                } else {
                    newIds.push(tagId);
                }
            } else {
                newIds = [tagId];
            }

            updateFilter(filter.tableId, {
                dimensionIds: newIds,
                selectAll: false,
            });
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        function TagItem({ columnIndex, rowIndex, style }: any) {
            const index = rowIndex * colCount + columnIndex;
            const filterDimension = dIds[index];
            if (!filterDimension || !filterDimension.active) {
                return null;
            }
            const dimensionKey = dimensionTagToEntityKey(filterDimension.id);
            return (
                <div style={style}>
                    <DimensionButton
                        filterDimension={filterDimension}
                        dimensionKey={dimensionKey}
                        action={() => toggleTag(filterDimension.id)}
                        style={{ width: colWidth - space, minHeight: rowHeight - space }}
                        displayType={data.displayType}
                    />
                </div>
            );
        }

        return (
            <DashboardTileContent ref={widgetRef}>
                <FixedSizeGrid
                    height={height || 0}
                    width={width || 0}
                    columnCount={colCount}
                    columnWidth={colWidth}
                    rowCount={rowCount}
                    rowHeight={rowHeight}
                    style={{
                        position: 'relative',
                    }}
                >
                    {TagItem}
                </FixedSizeGrid>
            </DashboardTileContent>
        );
    }
);

function DimensionButton({
    filterDimension,
    dimensionKey,
    style,
    action,
    displayType = DimensionValueNameDisplay.Name,
}: {
    filterDimension: SheetFilterDimensionRepresentationReduced;
    dimensionKey: EntityKey;
    style: CSSProperties;
    action: () => void;
    displayType: DimensionValueNameDisplay;
}) {
    const dimRef = useRef(null);

    const dimension = useEntity(dimensionKey);
    if (!dimension) return null;

    const title = getEntityLongName(dimension, displayType);
    return (
        <>
            <DimensionSlicerButton
                ref={dimRef}
                key={dimension.id}
                $active={filterDimension.selected}
                onClick={action}
                onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                style={style}
            >
                <DimensionSlicerButtonTitle>{title}</DimensionSlicerButtonTitle>
            </DimensionSlicerButton>

            <Tooltip anchorElementRef={dimRef} label={title} allowedPlacements={['top', 'top-start', 'top-end']} />
        </>
    );
}

DimensionSlicerTile.displayName = 'DimensionSlicerTile';
